import { renewToken, getIsLoggingOut } from './AuthServiceOidc';
import axios from 'axios';
import JwtService from '@common/JwtService';
import get from 'lodash/get';
import NProgress from 'nprogress/nprogress';
import isSystemRulesException from '@utils/is-system-rules-exception.util';
import isCanceledRequestError from '@utils/is-canceled-request-error';
import { providersActions } from '@common/GlobalProviders';
const lodash = require('lodash');
export const BASE_URL = process.env.VUE_APP_API_ENDPOINT || '';
/* eslint-disable camelcase */
let abortController = new AbortController();
const setupStartProgress = (axiosInstance) => {
    axiosInstance.interceptors.request.use((request) => {
        NProgress.start();
        return request;
    });
};
const cleanEmptyParams = (params) => {
    return lodash.omitBy(params, (v) => {
        return v === null || v === undefined || (Array.isArray(v) && !v.length);
    });
};
const setupStopProgress = (axiosInstance) => {
    axiosInstance.interceptors.response.use((response) => {
        NProgress.done(true);
        return response;
    }, (error) => {
        NProgress.done(true);
        return Promise.reject(error);
    });
};
const setupErrorHandlers = (axiosInstance) => {
    axiosInstance.interceptors.response.use(function (response) {
        return response;
    }, async function (error) {
        if (isCanceledRequestError(error)) {
            return Promise.reject(error);
        }
        const originalError = error;
        const originalRequest = error.config;
        if (originalRequest?.skipDefaultErrorNotification && error.response?.status !== 401) {
            return Promise.reject(error);
        }
        if (error.response) {
            if (error.response.status === 401 && !originalRequest._retry) {
                if (getIsLoggingOut()) {
                    return Promise.reject(error);
                }
                if (!JwtService.getToken()) {
                    providersActions.addNotification({
                        title: 'Error',
                        text: get(error.response, 'data.message') ||
                            get(error.response, 'data.error') ||
                            'No authorisation token exists',
                        type: 'error',
                    });
                }
                else if (error.response.data.error_code === 'invalid_device') {
                    providersActions.addNotification({
                        title: 'Error',
                        text: get(error.response, 'data.message') || get(error.response, 'data.error') || 'Invalid device ID',
                        type: 'error',
                    });
                }
                else {
                    originalRequest._retry = true;
                    providersActions.addNotification({
                        title: 'Please Wait',
                        text: 'The access token is being renewed. You might be made to login again.',
                        type: 'warn',
                    });
                    try {
                        const { access_token } = await renewToken();
                        StaffApiService.setAuthorizationHeader();
                        CommonApiService.setHeader();
                        originalRequest.headers.Authorization = `Bearer ${access_token}`;
                        providersActions.addNotification({
                            title: 'Success',
                            text: 'The access token has been renewed',
                            type: 'success',
                        });
                    }
                    catch (error) {
                        // eslint-disable no-console
                        console.error('ApiService:', originalError.response?.data?.message ||
                            originalError.response?.data?.error ||
                            originalError.message ||
                            originalError);
                        console.error('renewToken:', error.response?.data?.message || error.response?.data?.error || error.message || error);
                        // eslint-enable no-console
                        providersActions.addNotification({
                            title: 'Error',
                            text: 'The access token failed to renew',
                            type: 'error',
                        });
                    }
                    return axiosInstance(originalRequest);
                }
            }
            else if (error.response.status === 403) {
                providersActions.addNotification({
                    title: 'Error',
                    text: 'You are not allowed to perform this action',
                    type: 'error',
                });
            }
            else if (error.response.status === 404) {
                if (error.response.data.error !== 'booking_limit') {
                    let message = () => {
                        switch (true) {
                            case originalRequest.url.includes('v1/requests/dashboard/leg_assistance_items'):
                                return 'Please refresh the page to see updated information';
                            case originalRequest.url.includes('v1/events'):
                                return 'No such event exists';
                            default:
                                return 'The search returned no results';
                        }
                    };
                    providersActions.addNotification({
                        title: 'Error',
                        text: message(),
                        type: 'error',
                    });
                }
            }
            else if (error.response.status === 422 && !!error.response.data?.error) {
                // the system rule exception will be treated differently in component, this exception will not show a notify for user
                if (!isSystemRulesException(error)) {
                    providersActions.addNotification({
                        title: 'Error',
                        text: error.response.data.error,
                        type: 'error',
                    });
                }
            }
            else {
                providersActions.addNotification({
                    title: 'Error',
                    text: get(error.response, 'data.message') ||
                        get(error.response, 'data.error') ||
                        'There was a response error from the API server',
                    type: 'error',
                });
            }
        }
        else if (error.request && error.response === undefined) {
            abortController.abort();
            abortController = new AbortController();
            providersActions.addNotification({
                title: 'Error',
                text: 'A response was not returned from the API server',
                type: 'error',
            });
        }
        else {
            providersActions.addNotification({
                title: 'Error',
                text: 'An error occured when trying to create a request to the API server',
                type: 'error',
            });
        }
        return Promise.reject(error);
    });
};
const CommonApiInstance = axios.create({
    baseURL: `${BASE_URL}/common/api`,
});
const StaffApiInstance = axios.create({
    baseURL: `${BASE_URL}/staff/api`,
    timeout: 300000,
});
export const HealthCheckApiInstance = axios.create({
    baseURL: `${BASE_URL}/_healthcheck`,
});
const resourceMethods = (axiosInstance) => ({
    query(resource, params) {
        return axiosInstance.get(resource, params, { timeout: 2 });
    },
    get(resource, slug = '', params) {
        const url = slug.length ? `${resource}/${slug}` : resource;
        return axiosInstance.get(url, {
            signal: abortController.signal,
            ...params,
        });
    },
    post(resource, params, config) {
        return axiosInstance.post(`${resource}`, {
            signal: abortController.signal,
            ...params,
        });
    },
    update(resource, slug, params) {
        return axiosInstance.patch(`${resource}/${slug}`, params);
    },
    put(resource, params) {
        return axiosInstance.put(`${resource}`, params);
    },
    patch(resource, params, config) {
        return axiosInstance.patch(`${resource}`, params, config);
    },
    patchNoParams(resource, slug) {
        return axiosInstance.patch(`${resource}/${slug}`);
    },
    delete(resource) {
        return axiosInstance.delete(resource);
    },
});
export const CommonApiService = {
    setHeader() {
        CommonApiInstance.defaults.headers.common.Authorization = `Bearer ${JwtService.getToken()}`;
    },
    setInterceptors() {
        setupStartProgress(CommonApiService);
        setupStopProgress(CommonApiService);
        setupErrorHandlers(CommonApiService);
    },
    ...resourceMethods(CommonApiInstance),
};
export const StaffApiService = {
    setAuthorizationHeader() {
        StaffApiInstance.defaults.headers.common.Authorization = `Bearer ${JwtService.getToken()}`;
    },
    setDeviceIdHeader(deviceId) {
        StaffApiInstance.defaults.headers.common.device_id = deviceId;
    },
    setInterceptors() {
        setupStartProgress(StaffApiInstance);
        setupStopProgress(StaffApiInstance);
        setupErrorHandlers(StaffApiInstance);
    },
    ...resourceMethods(StaffApiInstance),
};
// Common services (used by PA and PAS)
export const StationsService = {
    query(params) {
        return CommonApiService.query('v1/stations', {
            params,
        });
    },
    queryStation(params) {
        return StaffApiService.query('v1/stations', {
            params,
        });
    },
    show(slug) {
        return CommonApiService.get('v1/stations', slug);
    },
    index(params) {
        return StaffApiService.query('v1/stations', {
            params,
        });
    },
    get(slug) {
        return StaffApiService.get('v1/stations', slug);
    },
    update(slug, params) {
        return StaffApiService.update('v1/stations', slug, params);
    },
};
export const AssistanceOptionsService = {
    get(params = {}) {
        return CommonApiService.query('v1/assistance_options', { params });
    },
    getDashboardAssistanceTypeFilterOptions() {
        return CommonApiService.query('v1/assistance_options', {
            params: {
                context: 'dashboard_assistance_type_filter',
            },
        });
    },
    getOtherAssistanceCategoriesGrid() {
        return StaffApiService.query('v1/assistances/other_assistance_categories_grid');
    },
};
export const GlobalOptionsService = {
    getCustomerTitles() {
        return CommonApiService.query('v1/configurations/titles');
    },
    getReservationsAutoCancelTimeInterval() {
        return CommonApiService.query('v1/configurations/reservations/auto_cancel_time_interval');
    },
    getRealTimeData(params) {
        return StaffApiService.query('v1/journey_legs/service_updates', {
            params: params,
        });
    },
    getRealTimeOJPData(params) {
        return StaffApiService.query('v1/journey_calling_points', {
            params: params,
        });
    },
};
// PAS only services
export const StationNotificationApi = {
    query(params) {
        return StaffApiService.query('v1/station_notifications', params);
    },
    post(params) {
        return StaffApiService.post('v1/station_notifications', params);
    },
    destroy(slug) {
        return StaffApiService.delete(`v1/station_notifications/${slug}`);
    },
    get(params) {
        return StaffApiService.query('v1/leg_station_notifications', {
            params,
        });
    },
};
export const NotesApiService = {
    post(params) {
        return StaffApiService.post('v1/notes', params);
    },
};
export const OrganisationApiService = {
    query(params) {
        return StaffApiService.query('v1/organisations', {
            params: params,
        });
    },
    get(slug) {
        return StaffApiService.get('v1/organisations', slug);
    },
    patch(slug, params) {
        return StaffApiService.update('v1/organisations', slug, params);
    },
    post(params) {
        return StaffApiService.post('v1/organisations', {
            params: params,
        });
    },
};
export const IncompleteReasonsApiService = {
    get() {
        return StaffApiService.get('v1/incomplete_reason_values');
    },
    create(journeyLegId, params) {
        return StaffApiService.post(`v1/journey_legs/${journeyLegId}/incomplete_reasons`, params);
    },
};
export const CheckInApiService = {
    create(body) {
        return StaffApiService.post('v1/check_in', body);
    },
    update(id, body) {
        return StaffApiService.patch(`v1/check_in/${id}`, body);
    },
};
export const JourneyLegsApiService = {
    query(params) {
        return StaffApiService.query('v1/journey_legs', {
            params: params,
        });
    },
    updateStatus(slug, params) {
        return StaffApiService.update('v1/journey_legs', slug, params);
    },
};
export const AssistanceDetailsService = {
    alight(assistanceDetailId) {
        return StaffApiInstance.patch(`v1/assistance_details/${assistanceDetailId}/alight`, {
            source: 'swi',
        });
    },
    incomplete(assistanceDetailId, params) {
        const { incomplete_reason_value_id, additional_notes, validation_reminder, validation_reminder_enabled } = params || {};
        return StaffApiInstance.patch(`v1/assistance_details/${assistanceDetailId}/incomplete`, {
            incomplete_reason_value_id,
            ...(additional_notes && { additional_notes }),
            ...(validation_reminder && { validation_reminder }),
            ...(validation_reminder_enabled !== undefined && { validation_reminder_enabled }),
            source: 'swi',
        });
    },
    unresolve(assistanceDetailId) {
        return StaffApiInstance.patch(`v1/assistance_details/${assistanceDetailId}/unresolve`, {
            source: 'swi',
        });
    },
    board(assistanceDetailId, params = {}) {
        const { seat, coach, additional_information, no_further_assistance, validation_reminder, validation_reminder_enabled, } = params;
        return StaffApiInstance.patch(`v1/assistance_details/${assistanceDetailId}/board`, {
            ...(seat && { seat }),
            ...(coach && { coach }),
            ...(additional_information && { additional_information }),
            ...(no_further_assistance !== undefined && { no_further_assistance }),
            ...(validation_reminder && { validation_reminder }),
            ...(validation_reminder_enabled !== undefined && { validation_reminder_enabled }),
            source: 'swi',
        });
    },
};
export const RequestsApiService = {
    query(params, options = {}) {
        return StaffApiService.query('v1/requests/dashboard', {
            params: params,
            ...options,
        });
    },
    show(id) {
        return StaffApiService.get(`v1/requests/dashboard/${id}`);
    },
    assign(params) {
        return StaffApiService.patch(`v1/requests/assign/${params.id}`, params);
    },
    unassign(params) {
        return StaffApiService.patch(`v1/requests/unassign/${params.id}`, params);
    },
    bulkAssign(params) {
        return StaffApiService.patch('v1/requests/bulk_assign', params, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            skipDefaultErrorNotification: true,
        });
    },
    bulkUnassign(params) {
        return StaffApiService.patch('v1/requests/bulk_unassign', params, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            skipDefaultErrorNotification: true,
        });
    },
    getAvailableUsersToAssign(params) {
        let query = '';
        if (params) {
            query += `q=${params}&per_page=25`;
        }
        return StaffApiService.get(`v1/requests/dashboard/available_users_to_assign?` + query);
    },
    getGroupedAvailableUsersToAssign({ request_id, q, page, per_page = 25 }) {
        return StaffApiService.query('v1/requests/dashboard/grouped_available_users_to_assign', {
            params: cleanEmptyParams({ request_id, q, page, per_page }),
        });
    },
    getAssignmentConflict({ request_id, user_id }) {
        return StaffApiService.query('v1/requests/dashboard/assignment_conflict', {
            params: cleanEmptyParams({ request_id, user_id }),
        });
    },
    getBulkAssignmentConflict({ request_ids, user_id }) {
        return StaffApiService.post('v1/requests/dashboard/bulk_assignment_conflict', { request_ids, user_id }, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            skipDefaultErrorNotification: true,
        });
    },
    getPassengerAccessConsideration(params) {
        return StaffApiService.get(`v1/requests/dashboard/customer_details?customer_id=${params.customer_id}`);
    },
    getPassengerJourneyAssistances(params) {
        return StaffApiService.get(`v1/requests/dashboard/leg_assistance_items?assistance_detail_id=${params.assistance_detail_id}`);
    },
    logHandover(id, params) {
        return StaffApiService.post(`v1/requests/handover/${id}`, params);
    },
};
export const BookingsService = {
    get(slug) {
        return StaffApiService.get(`v1/bookings/${slug}`);
    },
    getDuplicate(params) {
        return StaffApiService.post(`v1/bookings/${params.ref}/duplicate`, params);
    },
    getDuplicatePossibility(params) {
        return StaffApiService.post(`v1/bookings/${params.ref}/duplicate_possibility`, params);
    },
    getRecurring(params) {
        return StaffApiService.post(`v1/bookings/${params.ref}/recurring`, params);
    },
    create(params, version) {
        return StaffApiService.post(`${version}/bookings`, params);
    },
    update(slug, params, version) {
        return StaffApiService.update(`${version}/bookings`, slug, params);
    },
    confirm(params) {
        return StaffApiService.patch(`v1/bookings/confirm/${params.ref}`, params.data);
    },
    reject(slug, params) {
        return StaffApiService.update('v1/bookings/reject', slug, params);
    },
    incompleteBooking(params) {
        return StaffApiService.update('v1/incomplete_booking', params.ref, {
            incomplete_reasons: params.incomplete_reasons,
        });
    },
    cancelBooking(params) {
        return StaffApiService.patch(`v1/cancel_booking/${params.ref}`, params.data);
    },
    indexUnconfirmed(params) {
        return StaffApiService.query('v1/bookings/unconfirmed', {
            params: params,
        });
    },
    similarBookings(params) {
        return StaffApiService.query('v1/bookings/similar', {
            params,
        });
    },
    bulkSimilarBookings(bookingRefs) {
        return StaffApiService.post('v1/bookings/similar', { booking_refs: bookingRefs });
    },
    resendEmail(params) {
        return StaffApiService.post(`v1/bookings/resend_email`, params);
    },
};
export const CustomersService = {
    search(query) {
        return StaffApiService.post('v1/customers/search', query);
    },
    create(params) {
        return StaffApiService.post('v1/customers', params);
    },
    update(slug, params) {
        return StaffApiService.update('v1/customers', slug, params);
    },
    destroy(slug) {
        return StaffApiService.delete(`v1/customers/${slug}`);
    },
};
export const ReportFiles = {
    index() {
        return StaffApiService.get('v1/report_files');
    },
    cancel(slug) {
        return StaffApiService.patch(`v1/report_files/cancel/${slug}`);
    },
};
export const ReportService = {
    generate(params) {
        return StaffApiService.post('v2/reports/generate', cleanEmptyParams(params));
    },
    search(params) {
        return StaffApiService.post('v1/reports/search', cleanEmptyParams(params));
    },
    download(params) {
        return StaffApiService.post('v1/reports/download', cleanEmptyParams(params));
    },
    filterOptions() {
        return StaffApiService.get('v1/reports/filter_options');
    },
    illegalFilters() {
        return StaffApiService.get('v1/reports/illegal_filters');
    },
};
export const ReportSearchPresetService = {
    create(form) {
        return StaffApiService.post('v1/report_search_presets', form);
    },
    query(params) {
        return StaffApiService.query('v1/report_search_presets', params);
    },
    get(id) {
        return StaffApiService.get('v1/report_search_presets', id);
    },
    destroy(id) {
        return StaffApiService.delete(`v1/report_search_presets/${id}`);
    },
};
export const DashboardSearchPresetService = {
    create(params) {
        return StaffApiService.post('v1/dashboard_search_presets', params);
    },
    query() {
        return StaffApiService.query('v1/dashboard_search_presets');
    },
    get(id) {
        return StaffApiService.get('v1/dashboard_search_presets', id);
    },
    destroy(id) {
        return StaffApiService.delete(`v1/dashboard_search_presets/${id}`);
    },
};
export const JourneyPlannerService = {
    search(params) {
        return CommonApiService.post('v1/journey_planner', params);
    },
    searchByDirection(params) {
        return CommonApiService.post('v3/journey_planner', params);
    },
};
export const RarsApiService = {
    availability(params) {
        return StaffApiService.post('v2/rars/availability', params);
    },
    coaches(params) {
        return StaffApiService.query('v1/rars/coaches', { params });
    },
    cancel(rarsRef) {
        return StaffApiService.put(`v1/rars/${rarsRef}/cancel`);
    },
    recallBookingByRef(params) {
        return StaffApiService.query('v1/rars/recall_booking_by_ref', { params });
    },
    recallBookingByUnit(params) {
        return StaffApiService.query('v1/rars/recall_booking_by_unit', { params });
    },
};
export const PostcodeApi = {
    search(postcode) {
        return CommonApiService.get(`v1/postcodes/${postcode}`);
    },
};
export const VariablesApi = {
    getAll() {
        return StaffApiService.get('v1/variables');
    },
    update(params) {
        return StaffApiService.patch('v1/variables', params);
    },
};
export const ApiCommitHashService = {
    get() {
        return CommonApiService.get('v1/api_commit_hash');
    },
};
export const StatisticsService = {
    retrieveDataTrackings(params) {
        return StaffApiService.query('v1/statistics/data_tracking', {
            params,
        });
    },
    retrieveBrowsersList(params) {
        return StaffApiService.query('v1/statistics/browsers_list', {
            params,
        });
    },
    retrieveStatisticsReportTypes() {
        return StaffApiService.get('v1/statistics/reports');
    },
    downloadReport(params) {
        return StaffApiService.query('v2/statistics/reports/download', params);
    },
    generate(params) {
        return StaffApiService.query('v2/statistics/reports/generate', params);
    },
};
export const EventsService = {
    create(params) {
        return StaffApiService.post('v1/events', params);
    },
    destroy(id) {
        return StaffApiService.delete(`v1/events/${id}`);
    },
    get(id) {
        return StaffApiService.get(`v1/events`, id);
    },
    index(params) {
        return StaffApiService.query('v1/events', params);
    },
    update(id, params) {
        return StaffApiService.update('v1/events', id, params);
    },
};
export const EventNotificationsService = {
    cancel(id) {
        return StaffApiService.patchNoParams(`v1/event_notifications/cancel`, id);
    },
    create(params) {
        return StaffApiService.post('v1/event_notifications', params);
    },
    get(id) {
        return StaffApiService.get(`v1/event_notifications`, id);
    },
    index(params) {
        return StaffApiService.query('v1/event_notifications', { params });
    },
    update(id, params) {
        return StaffApiService.update('v1/event_notifications', id, params);
    },
};
export const BrowsersService = {
    create(params) {
        return StaffApiService.post('v1/browsers', params);
    },
};
export const LuggagesService = {
    options() {
        return CommonApiService.get('v1/luggages/options');
    },
};
export const StationDashboardService = {
    query(params) {
        return StaffApiService.query('v1/requests/station_dashboard', {
            params,
        });
    },
};
export const AssistanceChecklistService = {
    getChecklist(assistanceDetailId) {
        return StaffApiService.get(`v1/requests/${assistanceDetailId}/checklist`);
    },
    createAssistanceDetailChecklist(assistanceDetailId, params) {
        return StaffApiService.post(`v1/requests/${assistanceDetailId}/checklist`, params);
    },
    updateQuestionAnswers(assistanceDetailId, params) {
        return StaffApiService.patch(`v1/requests/${assistanceDetailId}/checklist`, params);
    },
};
export const AssistancePamEmailService = {
    get(assistanceDetailId) {
        return StaffApiService.get('v1/requests/station_dashboard', 'show_pam_email', {
            params: {
                assistance_detail_id: assistanceDetailId,
            },
        });
    },
    post(assistanceDetailId, params) {
        return StaffApiService.post(`v1/requests/${assistanceDetailId}/pam_email`, params);
    },
};
export const ShiftTimelineService = {
    query(params) {
        return StaffApiService.query('v1/requests/shift_timeline', {
            params,
        });
    },
};
export const CallsignBreakerService = {
    post({ start: start_time, end: end_time, callsignId: callsign_id, reason, date }) {
        return StaffApiService.post('v1/callsign_break', {
            start_time,
            end_time,
            callsign_id,
            date,
            ...(reason && {
                reason,
            }),
        });
    },
    put({ breakerId, start: start_time, end: end_time, callsignId: callsign_id, reason, date }) {
        return StaffApiService.put(`v1/callsign_break/${breakerId}`, {
            start_time,
            end_time,
            callsign_id,
            date,
            ...(reason && {
                reason,
            }),
        });
    },
    delete(breakerId) {
        return StaffApiService.delete(`v1/callsign_break/${breakerId}`);
    },
};
export const FeatureFlagService = {
    getFlags() {
        return CommonApiService.query('v1/feature_flags', {});
    },
};
